import _debounce from "lodash/debounce";
import _isEmpty from "lodash/isEmpty";
import _isEqual from "lodash/isEqual";

export default function AutoSaveManager(delay = 1500) {
  this.bulkData = {};
  this.delay = delay;
  this.noDelayFields = [];
  this.onSave = () => {};
  this.deferred = false;
  this.pendingData = null;

  this.commit = _debounce(changes => {
    if (!_isEqual(changes === this.bulkData)) {
      this.onSave(this.bulkData);
    }
  }, delay);
}

AutoSaveManager.prototype.setOptions = function(onSave, noDelayFields) {
  this.onSave = onSave;
  this.noDelayFields = noDelayFields;
};

AutoSaveManager.prototype.hasNoDelayFields = function(values) {
  const fields = this.noDelayFields;
  const keys = Object.keys(values);

  return (
    keys.length > 0 &&
    fields.some(field => {
      if (field instanceof RegExp) {
        return field.test(keys[0]);
      }
      return field === keys[0];
    })
  );
};

AutoSaveManager.prototype.hasValidValues = function(values) {
  return !_isEmpty(values) && !_isEqual(values, this.bulkData);
};

AutoSaveManager.prototype.push = function(changes) {
  if (!this.hasValidValues(changes)) return;
  this.bulkData = changes;

  if (this.deferred) {
    // Accumulate latest changes but don't start the debounce timer.
    // The save will be flushed when setDeferred(false) is called.
    this.pendingData = changes;
    this.commit.cancel();
    return;
  }

  this.commit(changes);

  if (this.hasNoDelayFields(changes)) {
    this.commit.flush();
  }
};

/**
 * Switches deferred mode on or off.
 *
 * When switching ON  (deferred=true): any in-flight debounce timer is cancelled
 * and the latest values are stored as pendingData.
 *
 * When switching OFF (deferred=false): if there is pendingData accumulated
 * during the deferred period, it is flushed immediately via onSave so that
 * no changes are lost.
 */
AutoSaveManager.prototype.setDeferred = function(deferred) {
  if (this.deferred === deferred) return;
  this.deferred = deferred;

  if (deferred) {
    // Cancel any running debounce and park the current bulkData as pending.
    if (!_isEmpty(this.bulkData)) {
      this.pendingData = { ...this.bulkData };
    }
    this.commit.cancel();
    return;
  }

  // deferred became false — flush any data that was accumulated.
  if (this.pendingData) {
    const data = this.pendingData;
    this.pendingData = null;
    this.onSave(data);
  }
};

AutoSaveManager.prototype.flushAutoSave = function() {
  return () => {
    if (this.deferred && this.pendingData) {
      const data = this.pendingData;
      this.pendingData = null;
      this.onSave(data);
      return;
    }
    this.commit.flush();
  };
};
